import { useUserGetUserSiteData } from "@crowdcoursing/api/openapi";
import { Box, Tab, Tabs } from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { type DateRangeValue } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { OrgGrowthData } from "~ui/templates/AdminTemplate/DashboardStats/OrgGrowthData";

import { DistrictOnboardingPanel } from "./DistrictOnboardingPanel";

const START_DATE_STORAGE_KEY = "orgGrowthTab.startDate";
const SUB_VIEW_STORAGE_KEY = "orgGrowthTab.subView";

type OrgMetricsSubView = "memberGrowth" | "onboarding";

const getInitialSubView = (): OrgMetricsSubView => {
  const stored = localStorage.getItem(SUB_VIEW_STORAGE_KEY);
  if (stored === "memberGrowth" || stored === "onboarding") {
    return stored;
  }
  return "memberGrowth";
};

const getInitialTimeRange = (): DateRangeValue => {
  try {
    const stored = localStorage.getItem(START_DATE_STORAGE_KEY);
    if (stored) {
      const startDate = DateTime.fromISO(stored);
      if (startDate?.isValid) {
        return { endDate: DateTime.now(), startDate };
      }
    }
  } catch {
    // Fall through to default
  }
  return {
    endDate: DateTime.now(),
    startDate: DateTime.now().minus({ days: 30 }),
  };
};

const useOrgGrowthDataProps = (queryEnabled: boolean) => {
  const [timeRange, setTimeRange] = useState<DateRangeValue>(getInitialTimeRange);

  const handleTimeRangeChange = (newTimeRange: DateRangeValue) => {
    setTimeRange(newTimeRange);
    if (newTimeRange.startDate) {
      localStorage.setItem(START_DATE_STORAGE_KEY, newTimeRange.startDate.toISO() ?? "");
    }
  };

  const queryParams = useMemo(
    () => ({
      end: timeRange.endDate?.toISO() ?? DateTime.now().toISO(),
    }),
    [timeRange],
  );

  const { data, status } = useUserGetUserSiteData(
    { queryParams },
    { enabled: queryEnabled, placeholderData: keepPreviousData },
  );

  return {
    data: data ?? null,
    onTimeRangeChange: handleTimeRangeChange,
    status,
    timeRange,
  };
};

export const OrgGrowthTab = () => {
  const [subView, setSubView] = useState<OrgMetricsSubView>(getInitialSubView);
  const props = useOrgGrowthDataProps(subView === "memberGrowth");

  return (
    <Box sx={{ maxWidth: "100%", minWidth: 0 }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          onChange={(_event, value: OrgMetricsSubView) => {
            setSubView(value);
            localStorage.setItem(SUB_VIEW_STORAGE_KEY, value);
          }}
          value={subView}
        >
          <Tab label="Member growth" value="memberGrowth" />
          <Tab label="Onboarding" value="onboarding" />
        </Tabs>
      </Box>
      {subView === "memberGrowth" ? <OrgGrowthData {...props} /> : <DistrictOnboardingPanel />}
    </Box>
  );
};
